import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionAction, RolePermissionsResponse } from "../interfaces";
import { getRolePermissions } from "../api/rolePermissions.api";
import { PermissionScope } from "@me-team/host/main/graphql/types";
import { Mode } from "../RoleDetailsPage";
import { buildCloneInitialDisplayName } from "../../../../Me-menu/utils/cloneNameUtils";
import { RoleDetailsPageService } from "../service/RoleDetailsPageService";

type UseRolePermissionsReturn = {
    permissions: RolePermissionsResponse | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    onChangeRoleName: (value: string) => void;
    onChangeRoleDescription: (value: string) => void;
    onChangePermissionScope: (moduleCode: string, actionCode: number, scopeValue: PermissionScope) => void;
    onChangePermissionAction: (moduleCode: string, actionCode: number, checked: boolean) => void;
    onChangePermissionCategory: (moduleCode: string, categoryCode: string, checked: boolean) => void;
    onChangeAllPermissions: (checked: boolean) => void;
    mode: Mode;
};

const updateActionsScope = (
    actions: PermissionAction[],
    actionCode: number,
    scopeValue: PermissionScope
): PermissionAction[] => {
    return actions.map((action) => {
        if (action.code === actionCode) {
            return {
                ...action,
                scope: action.scope
                    ? action.scope.map((option) => ({
                          ...option,
                          isActive: option.value === scopeValue,
                      }))
                    : null,
            };
        }

        if (action.children.length > 0) {
            return {
                ...action,
                children: action.children.map((child) => {
                    if (child.code === actionCode) {
                        return {
                            ...child,
                            scope: child.scope
                                ? child.scope.map((option) => ({
                                      ...option,
                                      isActive: option.value === scopeValue,
                                  }))
                                : null,
                        };
                    }

                    return child;
                }),
            };
        }

        return action;
    });
};

const updateActionsChecked = (
    actions: PermissionAction[],
    actionCode: number,
    checked: boolean
): PermissionAction[] => {
    return actions.map((action) => {
        if (action.code === actionCode) {
            return {
                ...action,
                isActive: checked,
                children: action.children.map((child) => ({
                    ...child,
                    isActive: checked ? child.isActive : false,
                })),
            };
        }

        if (action.children.length > 0) {
            const updatedChildren = action.children.map((child) => {
                if (child.code === actionCode) {
                    return {
                        ...child,
                        isActive: checked,
                    };
                }

                return child;
            });

            const hasUpdatedChild = updatedChildren.some((child, index) => child !== action.children[index]);

            if (hasUpdatedChild) {
                return {
                    ...action,
                    isActive: checked ? true : action.isActive,
                    children: updatedChildren,
                };
            }
        }

        return action;
    });
};

const updateActionScopeForCategoryToggle = (action: PermissionAction, checked: boolean): PermissionAction => {
    if (!action.supportsScope || !action.scope?.length) return action;

    const targetScope = checked ? PermissionScope.All : PermissionScope.Self;
    const isTargetPermissionScope = (scopeValue: PermissionScope) => String(scopeValue).toUpperCase() === targetScope;

    const hasTargetScope = action.scope.some((option) => isTargetPermissionScope(option.value));

    if (!hasTargetScope) return action;

    return {
        ...action,
        scope: action.scope.map((option) => ({
            ...option,
            isActive: isTargetPermissionScope(option.value),
        })),
    };
};

const updateCategoryActionsChecked = (actions: PermissionAction[], checked: boolean): PermissionAction[] => {
    return actions.map((action) =>
        updateActionScopeForCategoryToggle(
            {
                ...action,
                isActive: checked,
                children: action.children.map((child) =>
                    updateActionScopeForCategoryToggle(
                        {
                            ...child,
                            isActive: checked,
                        },
                        checked
                    )
                ),
            },
            checked
        )
    );
};

const useRolePermissions = (
    branchId: number | null,
    roleId: number | null,
    mode: Mode = "create"
): UseRolePermissionsReturn => {
    const { t, i18n } = useTranslation();
    const [permissions, setPermissions] = useState<RolePermissionsResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchPermissions = useCallback(async () => {
        if (!branchId || !i18n.language) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const data = await getRolePermissions(branchId, i18n.language, roleId, mode);

            if (mode === "clone") {
                setPermissions({
                    ...data,
                    role_info: {
                        ...data.role_info,
                        name: buildCloneInitialDisplayName(data.role_info.name, t),
                    },
                });
                return;
            }

            setPermissions(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error");
        } finally {
            setIsLoading(false);
        }
    }, [branchId, i18n.language, mode, roleId, t]);

    useEffect(() => {
        void fetchPermissions();
    }, [fetchPermissions]);

    const onChangeRoleName = useCallback((value: string) => {
        setPermissions((prevState) => {
            if (!prevState) {
                return prevState;
            }

            return {
                ...prevState,
                role_info: {
                    ...prevState.role_info,
                    name: value,
                },
            };
        });
    }, []);

    const onChangeRoleDescription = useCallback((value: string) => {
        setPermissions((prevState) => {
            if (!prevState) {
                return prevState;
            }

            return {
                ...prevState,
                role_info: {
                    ...prevState.role_info,
                    description: value,
                },
            };
        });
    }, []);

    const onChangePermissionScope = useCallback(
        (moduleCode: string, actionCode: number, scopeValue: PermissionScope) => {
            setPermissions((prevState) => {
                if (!prevState) {
                    return prevState;
                }

                return {
                    ...prevState,
                    modules: prevState.modules.map((module) => {
                        if (module.code !== moduleCode) {
                            return module;
                        }

                        return {
                            ...module,
                            categories: module.categories.map((category) => ({
                                ...category,
                                actions: updateActionsScope(category.actions, actionCode, scopeValue),
                            })),
                        };
                    }),
                };
            });
        },
        []
    );

    const onChangePermissionAction = useCallback((moduleCode: string, actionCode: number, checked: boolean) => {
        setPermissions((prevState) => {
            if (!prevState) {
                return prevState;
            }

            const updatedModules = prevState.modules.map((module) => {
                if (module.code !== moduleCode) {
                    return module;
                }

                return {
                    ...module,
                    categories: module.categories.map((category) => ({
                        ...category,
                        actions: updateActionsChecked(category.actions, actionCode, checked),
                    })),
                };
            });

            return {
                ...prevState,
                modules: RoleDetailsPageService.cascadeUnsatisfiedDependencies(updatedModules),
            };
        });
    }, []);

    const onChangePermissionCategory = useCallback((moduleCode: string, categoryCode: string, checked: boolean) => {
        setPermissions((prevState) => {
            if (!prevState) {
                return prevState;
            }

            const updatedModules = prevState.modules.map((module) => {
                if (module.code !== moduleCode) {
                    return module;
                }

                return {
                    ...module,
                    categories: module.categories.map((category) => {
                        if (category.code !== categoryCode) {
                            return category;
                        }

                        return {
                            ...category,
                            actions: updateCategoryActionsChecked(category.actions, checked),
                        };
                    }),
                };
            });

            return {
                ...prevState,
                modules: RoleDetailsPageService.cascadeUnsatisfiedDependencies(updatedModules),
            };
        });
    }, []);

    const onChangeAllPermissions = useCallback((checked: boolean) => {
        setPermissions((prevState) => {
            if (!prevState) {
                return prevState;
            }

            const updatedModules = prevState.modules.map((module) => ({
                ...module,
                categories: module.categories.map((category) => ({
                    ...category,
                    actions: updateCategoryActionsChecked(category.actions, checked),
                })),
            }));

            return {
                ...prevState,
                modules: RoleDetailsPageService.cascadeUnsatisfiedDependencies(updatedModules),
            };
        });
    }, []);

    return {
        permissions,
        isLoading,
        error,
        refetch: fetchPermissions,
        onChangeRoleName,
        onChangeRoleDescription,
        onChangePermissionScope,
        onChangePermissionAction,
        onChangePermissionCategory,
        onChangeAllPermissions,
        mode,
    };
};

export default useRolePermissions;
